import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { setOriginalValues } from '@/reducers/CurrentForm';
import _ from 'lodash';

/**
 * call hasDataChanged() to determine if backend call is needed
 * call resetChangeDetection() after successful BE submission to reset the change detection
 * @param keysToMonitor - the keys to monitor for changes in currentForm slice
 * @returns - hasDataChanged() to determine if backend call is needed
 * - resetChangeDetection() to reset the change detection
 */
export const useDataChangeDetection = (keysToMonitor: string[]) => {
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const originalValuesFromStore = useAppSelector((state) => state.CurrentForm.originalValues);
  const originalValues = useMemo(() => originalValuesFromStore || {}, [originalValuesFromStore]);
  const dispatch = useAppDispatch();

  // initialize original values if they don't exist for the monitored keys
  useEffect(() => {
    const missingKeys = keysToMonitor.filter((key) => !(key in originalValues));
    if (missingKeys.length > 0) {
      const newOriginalValues: Record<string, any> = {};
      missingKeys.forEach((key) => {
        newOriginalValues[key] = currentForm[key];
      });
      dispatch(setOriginalValues(newOriginalValues));
    }
  }, [keysToMonitor, originalValues, currentForm, dispatch]);

  const hasDataChanged = useCallback(() => {
    return keysToMonitor.some((key) => !_.isEqual(originalValues[key], currentForm[key]));
  }, [keysToMonitor, currentForm, originalValues]);

  const resetChangeDetection = useCallback(() => {
    const newOriginalValues: Record<string, any> = {};
    keysToMonitor.forEach((key) => {
      newOriginalValues[key] = currentForm[key];
    });
    dispatch(setOriginalValues(newOriginalValues));
  }, [keysToMonitor, currentForm, dispatch]);

  return { hasDataChanged, resetChangeDetection };
};
